/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Grid, Card, Typography, Skeleton, useTheme } from '@mui/material';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import { dashboardApi } from '../../../api/dashboardApi';
import useMounted from '../../../hooks/useMounted';
import { getTone, toneAlpha, grey, lightVeil, darkVeil } from '../../../theme/tones';

// Raccourcis : chaque carte affiche un compteur et redirige vers sa section au clic.
const SHORTCUTS = [
  { key: 'catalogue', title: 'Catalogue', unit: 'formations', to: '/dashboard/formations', field: 'formations', icon: <SchoolRounded sx={{ fontSize: '1.4rem' }} /> },
  { key: 'sessions', title: 'Sessions', unit: 'planifiées', to: '/dashboard/formationSession', field: 'sessions', icon: <CalendarMonthRounded sx={{ fontSize: '1.4rem' }} /> },
  { key: 'campagnes', title: 'Campagnes', unit: 'créées', to: '/dashboard/campaign', field: 'campaigns', icon: <SendRounded sx={{ fontSize: '1.4rem' }} /> }
];

const ShortcutCard = ({ loading, to, icon, title, count, unit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Card
      component={RouterLink}
      to={to}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2.5,
        height: '100%',
        textDecoration: 'none',
        borderRadius: '16px',
        backgroundColor: isDark ? toneAlpha('neutral', 0.6) : grey(0),
        border: `1px solid ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
        boxShadow: isDark ? `0 4px 20px -2px ${darkVeil(0.3)}` : `0 4px 20px -2px ${darkVeil(0.04)}`,
        transition: 'transform 0.25s ease, box-shadow 0.3s ease, border-color 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isDark ? `0 12px 26px -6px ${darkVeil(0.5)}` : `0 12px 26px -6px ${darkVeil(0.08)}`,
          borderColor: isDark ? lightVeil(0.16) : darkVeil(0.18)
        }
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: grey(0),
          backgroundColor: isDark ? lightVeil(0.12) : getTone('neutral').deepest
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'text.primary', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={90} height={20} animation="wave" />
        ) : (
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {(Number(count) || 0).toLocaleString('fr-FR')}
            </Box>{' '}
            {unit}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

const DashboardShortcutsRow = ({ selectedYear }) => {
  const mounted = useMounted();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getShortcuts = useCallback(async () => {
    try {
      const result = await dashboardApi.getShortcuts(localStorage.getItem('accessToken'), selectedYear);
      if (mounted.current && result) {
        setData(result);
      }
    } catch (err) {
      console.error('Erreur chargement raccourcis dashboard:', err);
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [mounted, selectedYear]);

  useEffect(() => {
    getShortcuts();
  }, [getShortcuts]);

  return (
    <Grid container spacing={2}>
      {SHORTCUTS.map((s) => (
        <Grid item key={s.key} xs={12} sm={6} md={4}>
          <ShortcutCard
            loading={loading}
            to={s.to}
            icon={s.icon}
            title={s.title}
            unit={s.unit}
            count={data[s.field]}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardShortcutsRow;
